import { useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { CopyButton } from '../components/CopyButton';
import { useCrumb } from '../lib/crumb';
import { DetailHead } from '../components/DetailHead';
import { DetailBody, RailCard } from '../components/DetailRail';
import { Ownership, type Owner } from '../components/Ownership';
import { Icon } from '../components/Icon';
import { RefreshButton } from '../components/RefreshButton';
import { ClusterDatabasesGraph, CLUSTER_GRAPH } from '../components/ClusterDatabasesGraph';
import type { ClusterGraph } from '../lib/clusterGraph';
import { useUrlState } from '../lib/useUrlState';

const CLUSTER = gql`
  query Cluster($name: String!) {
    cluster(name: $name) {
      id
      name
      owners {
        ownerId
        role
        displayName
        email
      }
      clusterId
      cloudProvider
      estimatedMonthlyCost
      doAccount
      region
      k8sVersion
      lbIp
      nodeCount
      ingressType
      certMode
      alloyOnboarded
      reachability
      namespaceCount
      confidence
      firstSeenAt
      lastSeenAt
      description
      nodeInfo
      health {
        monitored
        scrapeUpRatio
        nodesReady
        nodesTotal
        podsCrashlooping
        cpuUsedPct
        memUsedPct
        certExpiryDays
      }
      namespaces {
        id
        name
      }
    }
  }
`;

interface NodeDetail {
  name: string;
  pool: string | null;
  instanceType: string | null;
  region: string | null;
  kubeletVersion: string | null;
  ready: boolean;
  cpu: string | null;
  memory: string | null;
}
interface Cluster {
  id: string;
  name: string;
  owners: Owner[];
  clusterId?: string;
  cloudProvider?: string;
  estimatedMonthlyCost?: number;
  doAccount?: string;
  region?: string;
  k8sVersion?: string;
  lbIp?: string;
  nodeCount?: number;
  ingressType?: string;
  certMode?: string;
  alloyOnboarded?: boolean;
  reachability?: string;
  namespaceCount: number;
  confidence: string;
  firstSeenAt: string;
  lastSeenAt: string;
  description?: string;
  nodeInfo?: string;
  health?: {
    monitored: boolean;
    scrapeUpRatio?: number | null;
    nodesReady?: number | null;
    nodesTotal?: number | null;
    podsCrashlooping?: number | null;
    cpuUsedPct?: number | null;
    memUsedPct?: number | null;
    certExpiryDays?: number | null;
  };
  namespaces: { id: string; name: string }[];
}
interface Data {
  cluster: Cluster | null;
}
interface GraphData {
  clusterGraph: ClusterGraph | null;
}

type Tab = 'overview' | 'resources' | 'databases' | 'extensions';
const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'resources', label: 'Resources' },
  { key: 'databases', label: 'Databases' },
  { key: 'extensions', label: 'Extensions' },
];

export function ClusterDetailPage() {
  const { name = '' } = useParams();
  // The active tab is the READER's state, not the component's: `?tab=databases` is a
  // link you can paste, and F5 lands on the same tab instead of throwing you back to
  // Overview (AC-14). `useUrlState` keeps the default out of the address bar.
  const [tabParam, setTab] = useUrlState('tab', 'overview');
  const tab = (TABS.some((t) => t.key === tabParam) ? tabParam : 'overview') as Tab;
  const { data, loading, error, refetch } = useQuery<Data>(CLUSTER, {
    variables: { name },
    fetchPolicy: 'cache-and-network',
  });

  const c = data?.cluster;

  // A cluster can be REAL and absent from the inventory: `agents` registers one the
  // moment an agent reports, while the k8s scan only ever walks the clusters its own
  // credentials reach (qa scans one; on prod, any cluster that registers before its
  // first scan). Gating the whole page on `cluster(name)` told those people the
  // cluster does not exist, while `clusterGraph` was already answering for it —
  // so when the inventory row is missing we ask the graph instead (AC-2).
  // `skip` keeps this off the wire entirely whenever the row DOES exist, which is
  // what AC-14 is about: an Overview reader never fires `clusterGraph`.
  const { data: graphData, loading: graphLoading } = useQuery<GraphData>(CLUSTER_GRAPH, {
    variables: { cluster: name },
    fetchPolicy: 'cache-and-network',
    skip: !data || data.cluster != null,
  });
  const graph = graphData?.clusterGraph ?? null;
  // The server says so itself ("not in inventory"); one edge is the fallback signal,
  // because an answer with permissions in it is an answer about a real cluster.
  const agentsOnly =
    !c && graph != null && (graph.focus?.sub === 'not in inventory' || (graph.edges?.length ?? 0) > 0);

  const pools = useMemo(() => groupPools(c?.nodeInfo), [c?.nodeInfo]);

  // Breadcrumb tail — the topbar knows the route, not the entity.
  // MUST sit above the early returns: called after them, it would be a
  // conditional hook and React would throw once the data arrived.
  useCrumb(c?.name ?? (agentsOnly ? name : undefined));

  if (loading && !data) return <div className="page">Loading…</div>;
  if (error) return <div className="page muted">Failed to load: {error.message}</div>;
  if (!c) {
    if (graphLoading && !graphData) return <div className="page">Loading…</div>;
    if (agentsOnly) return <AgentsOnlyCluster name={name} />;
    return (
      <div className="page">
        <p className="muted">Cluster “{name}” not found.</p>
        <Link to="/clusters">← Back to clusters</Link>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="detail-back">
        <Link to="/clusters" className="muted">
          ← Clusters
        </Link>
      </div>
      <DetailHead
        name={c.name}
        badge={
          <span className={`badge ${c.reachability === 'reachable' ? 'confirmed' : 'inferred'}`}>
            {c.reachability === 'reachable' ? 'reachable' : 'unreachable'}
          </span>
        }
        facts={[
          c.region,
          c.k8sVersion && `k8s ${c.k8sVersion}`,
          c.nodeCount != null && `${c.nodeCount} node${c.nodeCount === 1 ? '' : 's'}`,
          `${c.namespaceCount} namespace${c.namespaceCount === 1 ? '' : 's'}`,
        ]}
        scannedAt={c.lastSeenAt}
        actions={<CopyButton value={c.name} />}
      />

      <DetailBody
        rail={
          <>
            {/* No RailCard wrapper: Ownership renders its own heading. */}
            <div className="rail-card">
              <Ownership
                subjectType="cluster"
                subjectId={c.id}
                owners={c.owners}
                onChanged={() => void refetch()}
              />
            </div>
            <RailCard title="Access">
              <div className="stack-sm">
                {c.lbIp && (
                  <div className="ip-cell">
                    {/* An IP is an identifier, not a code snippet — phase 6 §1 mono policy. */}
                    <span>{c.lbIp}</span>
                    <CopyButton value={c.lbIp} />
                  </div>
                )}
                <span className="muted text-2xs">{c.doAccount ?? 'account unknown'}</span>
                <Link
                  to={`/tasks/new?template=access&type=cluster&entityId=${c.id}&name=${encodeURIComponent(c.name)}`}
                >
                  Request access
                </Link>
              </div>
            </RailCard>
            <RailCard title="Elsewhere">
              <div className="stack-sm">
                <Link to={`/repos?view=graph&cluster=${encodeURIComponent(c.name)}`}>
                  What runs here (graph)
                </Link>
                {/* Named for what the screen still holds. It listed egress addresses until
                    2026-08-26; it now lands on the Atlas projects, whose drawers carry the
                    access lists. */}
                <Link to="/cluster-access">Atlas access lists</Link>
                <Link to="?tab=databases">Databases this cluster can reach</Link>
              </div>
            </RailCard>
          </>
        }
      >
      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview c={c} />}
      {/* Mounted only while the tab is active, so `clusterGraph` — seven queries on
          the api — is never fired for someone reading the Overview (AC-14). */}
      {tab === 'databases' && (
        <ClusterDatabasesGraph clusterName={c.name} clusterUuid={c.clusterId ?? null} />
      )}
      {tab === 'resources' && (
        <Resources pools={pools} refetch={() => refetch().catch(() => undefined)} />
      )}
      {tab === 'extensions' && <Extensions c={c} />}
      </DetailBody>
    </div>
  );
}

/**
 * The reduced page for a cluster we know only from its agents.
 *
 * There is no inventory row, so there is nothing to put on Overview, Resources or
 * Extensions — every one of those tabs is built from scan fields. Databases is the
 * one question that CAN be answered (the graph is keyed by name), so it is the only
 * tab, and the notice says plainly why the rest is missing rather than leaving a
 * thinner page to be read as a bug.
 */
function AgentsOnlyCluster({ name }: { name: string }) {
  return (
    <div className="page">
      <div className="detail-back">
        <Link to="/clusters" className="muted">
          ← Clusters
        </Link>
      </div>
      <DetailHead
        name={name}
        badge={<span className="badge inferred">not in inventory</span>}
        actions={<CopyButton value={name} />}
      />
      <p className="muted" data-testid="cluster-not-in-inventory">
        Not in the inventory yet — known from its agents only; DigitalOcean k8s rules cannot be
        matched until a scan records this cluster
      </p>

      <DetailBody
        rail={
          <RailCard title="Elsewhere">
            <div className="stack-sm">
              <Link to={`/repos?view=graph&cluster=${encodeURIComponent(name)}`}>
                What runs here (graph)
              </Link>
              <Link to="/cluster-access">Atlas access lists</Link>
            </div>
          </RailCard>
        }
      >
        <div className="tabs">
          <button type="button" className="active">
            Databases
          </button>
        </div>
        {/* clusterUuid is null by construction: the uuid lives on the inventory row we
            do not have, and the tab's own hint says what that costs. */}
        <ClusterDatabasesGraph clusterName={name} clusterUuid={null} />
      </DetailBody>
    </div>
  );
}

function Overview({ c }: { c: Cluster }) {
  const facts: [string, React.ReactNode][] = [
    ['Estimated cost', c.estimatedMonthlyCost != null ? `~$${c.estimatedMonthlyCost} / mo` : '—'],
    ['Cluster ID', c.clusterId ? <span className="ip-cell">{c.clusterId}<CopyButton value={c.clusterId} /></span> : '—'],
    ['Cloud Provider', c.cloudProvider ?? '—'],
    ['DO account', c.doAccount ?? '—'],
    ['Region', c.region ?? '—'],
    ['Version', c.k8sVersion ? <span className="id-compact">{c.k8sVersion}</span> : '—'],
    ['LB IP', c.lbIp ? <span className="ip-cell">{c.lbIp}<CopyButton value={c.lbIp} /></span> : '—'],
    ['Nodes', c.nodeCount ?? '—'],
    ['Namespaces', c.namespaceCount],
    ['Last scanned', new Date(c.lastSeenAt).toLocaleString()],
  ];
  return (
    <>
      {c.description && <p className="detail-desc">{c.description}</p>}
      <div className="kv-grid">
        {facts.map(([k, v]) => (
          <div className="kv" key={k}>
            <div className="kv-k">{k}</div>
            <div className="kv-v">{v}</div>
          </div>
        ))}
      </div>
      {c.estimatedMonthlyCost != null && (
        <p className="muted text-2xs" style={{ marginTop: 8 }}>
          Cost is an estimate from node instance-type list prices, not actual DO billing.
        </p>
      )}

      <h3 className="section">Live health</h3>
      {c.health?.monitored ? (
        <div className="kv-grid">
          {(
            [
              ['Scrape up', c.health.scrapeUpRatio != null ? `${Math.round(c.health.scrapeUpRatio * 100)}%` : '—'],
              ['Nodes ready', c.health.nodesTotal != null ? `${c.health.nodesReady ?? 0}/${c.health.nodesTotal}` : '—'],
              ['Crashlooping pods', c.health.podsCrashlooping ?? 0],
              ['CPU used', c.health.cpuUsedPct != null ? `${c.health.cpuUsedPct}%` : '—'],
              ['Mem used', c.health.memUsedPct != null ? `${c.health.memUsedPct}%` : '—'],
              ['Cert expiry', c.health.certExpiryDays != null ? `${Math.round(c.health.certExpiryDays)} days` : '—'],
            ] as [string, React.ReactNode][]
          ).map(([k, v]) => (
            <div className="kv" key={k}>
              <div className="kv-k">{k}</div>
              <div className="kv-v">{v}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="muted">
          Not monitored — no Prometheus scrape onboarded for this cluster (or Grafana not
          configured). Live health appears once Alloy is shipping metrics.
        </p>
      )}

      <h3 className="section">Namespaces ({c.namespaces.length})</h3>
      <div className="ns-grid">
        {c.namespaces.map((n) => (
          <span key={n.id} className="badge id-compact">
            {n.name}
          </span>
        ))}
      </div>
    </>
  );
}

function Resources({ pools, refetch }: { pools: Pool[]; refetch: () => unknown }) {
  if (pools.length === 0)
    return <p className="muted">No node data (cluster may be unreachable).</p>;
  return (
    <>
      <div className="section-head">
        <h3>Node pools ({pools.length})</h3>
        <span className="spacer" />
        <RefreshButton refetch={refetch} />
      </div>
      {pools.map((p) => (
        <div key={p.name} className="pool">
          <div className="pool-head">
            <strong>{p.name}</strong>
            <span className="badge">{p.instanceType}</span>
            <span className="muted">
              {p.nodes.length} node{p.nodes.length === 1 ? '' : 's'}
            </span>
          </div>
          <table>
            <thead>
              <tr>
                <th>VM (node)</th>
                <th>Size</th>
                <th>CPU</th>
                <th>Memory</th>
                <th>Kubelet</th>
                <th>Ready</th>
              </tr>
            </thead>
            <tbody>
              {p.nodes.map((n) => (
                <tr key={n.name}>
                  <td>{n.name}</td>
                  <td className="muted">{n.instanceType ?? '—'}</td>
                  <td className="muted">{n.cpu ?? '—'}</td>
                  <td className="muted">{fmtMem(n.memory)}</td>
                  <td className="muted">{n.kubeletVersion ?? '—'}</td>
                  <td>{n.ready ? <Icon name="check" /> : <Icon name="cross" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

interface Ext {
  name: string;
  detail: string;
}
function Extensions({ c }: { c: Cluster }) {
  const ns = new Set(c.namespaces.map((n) => n.name));
  const exts: Ext[] = [];
  if (c.ingressType === 'ingress-nginx' || ns.has('ingress-nginx'))
    exts.push({ name: 'Nginx Ingress Controller', detail: `Ingress controller${c.lbIp ? ` · LB ${c.lbIp}` : ''}` });
  if (c.ingressType === 'traefik')
    exts.push({ name: 'Traefik', detail: `Ingress/proxy${c.lbIp ? ` · LB ${c.lbIp}` : ''}` });
  if (c.certMode === 'cert-manager' || ns.has('cert-manager'))
    exts.push({ name: 'Cert-Manager', detail: 'Automated TLS certificate issuance (Let’s Encrypt)' });
  if (c.alloyOnboarded || ns.has('alloy'))
    exts.push({ name: 'Grafana Alloy', detail: 'Observability agent — ships logs/metrics to the stack' });
  if (ns.has('monitoring'))
    exts.push({ name: 'Monitoring (Prometheus)', detail: 'In-cluster metrics stack (ns monitoring)' });
  if (ns.has('kubernetes-dashboard'))
    exts.push({ name: 'Kubernetes Dashboard', detail: 'Web UI for the cluster (ns kubernetes-dashboard)' });

  if (exts.length === 0)
    return <p className="muted">No known extensions detected (from ingress type, cert-manager, alloy, or add-on namespaces).</p>;

  return (
    <>
      <h3 className="section">Extensions ({exts.length})</h3>
      <div className="ext-grid">
        {exts.map((e) => (
          <div className="ext-card" key={e.name}>
            <div className="ext-name">
              <span className="dot" /> {e.name}
            </div>
            <div className="muted text-xs" style={{ marginTop: 4 }}>
              {e.detail}
            </div>
          </div>
        ))}
      </div>
      <p className="muted text-2xs" style={{ marginTop: 10 }}>
        Detected from ingress type + add-on namespaces. Deeper add-on inventory (Helm releases,
        operators) is a follow-up.
      </p>
    </>
  );
}

interface Pool {
  name: string;
  instanceType: string | null;
  nodes: NodeDetail[];
}
function groupPools(nodeInfoJson?: string): Pool[] {
  if (!nodeInfoJson) return [];
  let nodes: NodeDetail[] = [];
  try {
    nodes = JSON.parse(nodeInfoJson).nodes ?? [];
  } catch {
    return [];
  }
  const byPool = new Map<string, Pool>();
  for (const n of nodes) {
    const key = n.pool || '(no pool)';
    if (!byPool.has(key)) byPool.set(key, { name: key, instanceType: n.instanceType, nodes: [] });
    byPool.get(key)!.nodes.push(n);
  }
  return [...byPool.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** "32872676Ki" → "31 GB". */
function fmtMem(mem: string | null): string {
  if (!mem) return '—';
  const m = mem.match(/^(\d+)Ki$/);
  if (m) return `${Math.round(Number(m[1]) / 1024 / 1024)} GB`;
  return mem;
}
